import { createElement, useContext, useEffect, useCallback, useRef } from 'react';
import { defineMessages } from 'react-intl';

import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types/emitter';

import { useEmbeddedComments } from '@confluence/embedded-confluence-allowed-features/entry-points/comments.ts';
import { fg } from '@confluence/feature-gating';
import {
	useObjectSidebarActions,
	usePanel,
	useIsObjectSidebarShown,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { PanelName } from '@confluence/object-sidebar-api/entry-points/PanelName.ts';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter.ts';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter.ts';
import { PageMode, ContentStatus } from '@confluence/page-utils/entry-points/enums.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus.ts';
import { useAnnotationsDispatch } from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import {
	useCommentsPanelActions,
	type ViewValues,
	type SortValues,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { usePageContentId } from '@confluence/page-context';
import { useAddCommentPermissionCheck } from '@confluence/comments-hooks/entry-points/useAddCommentPermissionCheck.ts';
import {
	isSpaceOverview,
	isDatabase,
	isWhiteboard,
	isSlides,
} from '@confluence/named-routes/entry-points/routes-utils.ts';
import { SIDEBAR_COMMENTS_PANEL_LOAD_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import {
	useCommentsContentActions,
	useCommentsContentState,
} from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import type { PanelProps } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { LoadableLazy } from '@confluence/loadable';

import type { CommentsPanelWrapperProps } from './CommentsPanelWrapper.tsx';
import { CommentsPanelPermissionContext } from './CommentsPanelPermissionContext.ts';

const CommentsPanelFilterMenu = LoadableLazy<PanelProps>({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-CommentsPanelFilterMenu" */ './CommentsPanelFilterMenu.tsx'
			)
		).CommentsPanelFilterMenu,
});

const CommentsPanelFooter = LoadableLazy<PanelProps>({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-CommentsPanelFooter" */ './CommentsPanelFooter.tsx'
			)
		).CommentsPanelFooter,
});

const CommentsPanelHeaderIcon = LoadableLazy<PanelProps>({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-CommentsPanelHeaderIcon" */ './CommentsPanelHeaderIcon.tsx'
			)
		).CommentsPanelHeaderIcon,
});

const CommentsPanelWrapper = LoadableLazy<CommentsPanelWrapperProps>({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-CommentsPanelWrapper" */ './CommentsPanelWrapper.tsx'
			)
		).CommentsPanelWrapper,
});

const CommentsPanelHeaderActions = LoadableLazy<PanelProps>({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-CommentsPanelHeaderActions" */ './CommentsPanelHeaderActions.tsx'
			)
		).CommentsPanelHeaderActions,
});

export const i18n: {
	commentsPanelHeaderLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	commentsPanelHeaderLabel: {
		id: 'comments-panel.panel.header.label',
		defaultMessage: 'Comments',
		description:
			'The text is shown as the header label at the top of the comments panel sidebar, identifying the panel to the user.',
	},
});

export const preloadCommentsPanel = (pageMode: PageMode): void => {
	// Always preload the core components
	// eslint-disable-next-line @typescript-eslint/no-floating-promises -- Intentional fire-and-forget preload
	CommentsPanelWrapper.preload();
	// eslint-disable-next-line @typescript-eslint/no-floating-promises -- Intentional fire-and-forget preload
	CommentsPanelFilterMenu.preload();
	// eslint-disable-next-line @typescript-eslint/no-floating-promises -- Intentional fire-and-forget preload
	CommentsPanelHeaderIcon.preload();
	// eslint-disable-next-line @typescript-eslint/no-floating-promises -- Intentional fire-and-forget preload
	CommentsPanelHeaderActions.preload();

	// Only preload footer in view mode (not edit mode)
	if (pageMode !== PageMode.EDIT) {
		// eslint-disable-next-line @typescript-eslint/no-floating-promises -- Intentional fire-and-forget preload
		CommentsPanelFooter.preload();
	}
};

export const useShowCommentsPanel = (
	preloadedCanAddComments?: boolean,
): {
	isCommentsPanelShown: boolean;
	showCommentsPanel: (options?: { openWithView?: ViewValues; initialSort?: SortValues }) => void;
	hideCommentsPanel: (afterCloseCallback?: (() => void) | undefined) => Promise<void>;
} => {
	const isSpaceOverviewPage = isSpaceOverview();
	const isDatabaseView = isDatabase();
	const isWhiteboardView = isWhiteboard();
	const isSlidesView = isSlides();
	const { contentStatus } = useContentStatus();

	const isObjectSidebarShown = useIsObjectSidebarShown();
	const panel = usePanel();
	const { showObjectSidebar, hideObjectSidebar } = useObjectSidebarActions();

	const { setViewProps, setEditorViewProps } = useAnnotationsDispatch();

	const { setCurrentView, setCurrentlySelectedThreadKey, setReplyToCommentId } =
		useCommentsPanelActions();
	const [contentId] = usePageContentId();
	const { canAddComments } = useAddCommentPermissionCheck(
		contentId ?? '',
		true,
		preloadedCanAddComments,
	);

	const pageMode = useGetPageMode();
	const eventEmitter =
		pageMode === PageMode.VIEW
			? getRendererAnnotationEventEmitter()
			: getEditorAnnotationEventEmitter();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const { showModal } = useDialogs();
	const { hasContentChanged } = useCommentsContentState();
	const { resetContentChanged } = useCommentsContentActions();

	const allowComments = useEmbeddedComments();

	// a ref is needed here because of a closure issue with canClosePanel
	const hasContentChangedRef = useRef(hasContentChanged);
	useEffect(() => {
		hasContentChangedRef.current = hasContentChanged;
	}, [hasContentChanged]);

	const onPanelCloseFunction = () => {
		setCurrentView(undefined);

		// Unselect active annotations on the page
		if (pageMode === PageMode.VIEW) {
			eventEmitter.emit(AnnotationUpdateEvent.DESELECT_ANNOTATIONS);
			eventEmitter.emit(AnnotationUpdateEvent.REMOVE_ANNOTATION_FOCUS);
			setViewProps(null);
		} else {
			eventEmitter.emit('setselectedannotation', '');
			setEditorViewProps(null);
		}

		// clear selected comment thread in the comments panel
		setCurrentlySelectedThreadKey('');
		setReplyToCommentId();
	};

	const onPanelClose = useCallback(onPanelCloseFunction, [
		setCurrentView,
		pageMode,
		eventEmitter,
		setViewProps,
		setEditorViewProps,
		setCurrentlySelectedThreadKey,
		setReplyToCommentId,
	]);

	const canClosePanel = useCallback(async () => {
		if (hasContentChangedRef.current && !fg('confluence_comments_fix_duplicate_modal')) {
			return new Promise<boolean>((resolve) => {
				showModal(CommentWarningDialog, {
					onConfirm: () => {
						resetContentChanged && resetContentChanged();
						resolve(true);
					},
					onCancel: () => {
						resolve(false);
					},
				});
			});
		} else {
			return true;
		}
	}, [resetContentChanged, showModal]);

	const showCommentsPanelFunction = ({
		openWithView,
		initialSort,
	}: {
		openWithView?: ViewValues;
		initialSort?: SortValues;
	} = {}): void => {
		if (!allowComments) {
			return;
		}

		experienceTracker.start({
			name: SIDEBAR_COMMENTS_PANEL_LOAD_EXPERIENCE,
			attributes: {
				openWithView,
				contentId,
				pageMode,
			},
		});

		const loadFooterComments = pageMode !== PageMode.EDIT;

		// Drag-to-resize on page surfaces only (same exclusions as the panel
		// itself). `expValEquals` is the LAST clause per
		// `confluence-feature-gating/no-preconditioning` so exposure is only
		// recorded for users who actually meet the surface preconditions.
		const isResizableSurface =
			!isSpaceOverviewPage &&
			!isDatabaseView &&
			!isWhiteboardView &&
			!isSlidesView &&
			expValEquals('confluence_fe_comments_panel_resize', 'isEnabled', true);

		const showFooter =
			canAddComments &&
			contentStatus !== ContentStatus.DRAFT &&
			loadFooterComments &&
			!isSpaceOverviewPage &&
			!isDatabaseView &&
			!isWhiteboardView &&
			!isSlidesView;

		showObjectSidebar(
			{
				id: PanelName.CommentsPanel,
				headerComponentElements: {
					HeaderIcon: CommentsPanelHeaderIcon,
					headerLabel: i18n.commentsPanelHeaderLabel,
					HeaderRightAlignedElement: CommentsPanelHeaderActions,
				},
				BodyComponent: () =>
					createElement(
						CommentsPanelPermissionContext.Provider,
						{ value: preloadedCanAddComments },
						createElement(CommentsPanelWrapper, {
							openWithView,
							initialSort,
						}),
					),
				FooterComponent: showFooter ? CommentsPanelFooter : undefined,
				closeOptions: {
					canClosePanel,
					onPanelClose,
				},
				isResizable: isResizableSurface,
			},
			'push',
		);
	};

	const showCommentsPanel = useCallback(showCommentsPanelFunction, [
		allowComments,
		canAddComments,
		canClosePanel,
		contentId,
		contentStatus,
		experienceTracker,
		isDatabaseView,
		isSpaceOverviewPage,
		isWhiteboardView,
		isSlidesView,
		onPanelClose,
		pageMode,
		preloadedCanAddComments,
		showObjectSidebar,
	]);

	// NOTE: Exposing the hide function for now while we have the CommentButton
	return {
		isCommentsPanelShown: isObjectSidebarShown && panel?.id === PanelName.CommentsPanel,
		showCommentsPanel,
		hideCommentsPanel: hideObjectSidebar,
	};
};
